import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import {
  Loader2, LogOut, RefreshCw, Search, Download, Trash2, Mail, Phone,
  Briefcase, DollarSign, MessageSquare, Send
} from "lucide-react";
import { crm, STATUS_META, STATUS_ORDER, exportLeadsCsv } from "@/lib/crmApi";
import { LOGO_URL } from "@/data/content";

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || { label: status, color: "#8799B3" };
  return (
    <span className="inline-flex items-center gap-2 rounded-sm border px-2.5 py-1 text-xs font-medium"
      style={{ borderColor: `${m.color}55`, color: m.color, background: `${m.color}11` }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: m.color }} />
      {m.label}
    </span>
  );
};

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!email || !password) return toast.error("Enter your email and password.");
    setLoading(true);
    try {
      const { data } = await crm.post("/auth/login", { email, password });
      localStorage.setItem("mag_token", data.token);
      onLogin(data.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  const goGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid min-h-screen place-items-center bg-navy px-6">
      <form onSubmit={submit} className="w-full max-w-sm rounded-sm border border-white/10 bg-white/[0.03] p-8">
        <img src={LOGO_URL} alt="Matlock" className="mx-auto h-14 w-auto" />
        <h1 className="mt-6 text-center font-serif text-3xl text-white">Team CRM</h1>
        <p className="mt-1 text-center text-sm text-white/50">Sign in to manage consultation leads.</p>
        <label className="mt-8 block text-xs uppercase tracking-[0.15em] text-white/50">Email</label>
        <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="you@matlockadvisorygroup.com"
          className="mt-2 bg-transparent border-white/20 text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-gold" />
        <label className="mt-5 block text-xs uppercase tracking-[0.15em] text-white/50">Password</label>
        <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          className="mt-2 bg-transparent border-white/20 text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-gold" />
        <button data-testid="login-submit" type="submit" disabled={loading}
          className="mt-7 flex w-full items-center justify-center gap-2 rounded-sm bg-gold py-3 text-sm font-medium text-navy-deep hover:bg-gold-hover transition-colors duration-300">
          {loading ? <Loader2 className="animate-spin" size={16} /> : null} Sign In
        </button>

        <div className="my-6 flex items-center gap-4">
          <div className="h-px flex-1 bg-white/15" />
          <span className="text-xs uppercase tracking-[0.15em] text-white/40">or</span>
          <div className="h-px flex-1 bg-white/15" />
        </div>

        <button data-testid="login-google" type="button" onClick={goGoogle}
          className="flex w-full items-center justify-center gap-3 rounded-sm border border-white/20 bg-white py-3 text-sm font-medium text-navy-deep hover:bg-white/90 transition-colors duration-300">
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
          Continue with Google
        </button>
      </form>
    </div>
  );
}

function LeadDialog({ lead, team, onClose, onChange, onDelete }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  if (!lead) return null;

  const setStatus = async (status) => {
    try {
      const { data } = await crm.patch(`/leads/${lead.id}`, { status });
      onChange(data);
      toast.success("Status updated.");
    } catch { toast.error("Could not update status."); }
  };
  const setAssigned = async (assigned_to) => {
    try {
      const { data } = await crm.patch(`/leads/${lead.id}`, { assigned_to });
      onChange(data);
      toast.success("Assignment updated.");
    } catch { toast.error("Could not assign."); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      const { data } = await crm.post(`/leads/${lead.id}/notes`, { text: note.trim() });
      onChange(data);
      setNote("");
    } catch { toast.error("Could not add note."); }
    finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await crm.delete(`/leads/${lead.id}`);
      onDelete(lead.id);
      toast.success("Lead deleted.");
    } catch { toast.error("Could not delete."); }
  };

  const field = (Icon, label, value) => (
    <div className="flex items-start gap-3">
      <Icon size={16} className="mt-0.5 shrink-0 text-gold" />
      <div><p className="text-xs uppercase tracking-[0.12em] text-white/40">{label}</p>
        <p className="text-sm text-white/85">{value || "—"}</p></div>
    </div>
  );
  const selTrig = "bg-transparent border-white/20 text-white h-9 focus:ring-0 focus:border-gold";

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="lead-dialog" className="max-w-2xl border-white/10 bg-navy-soft text-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl">{lead.name}</DialogTitle>
          <DialogDescription className="text-white/50">
            Submitted {new Date(lead.created_at).toLocaleString()}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-3">
          <Select value={lead.status} onValueChange={setStatus}>
            <SelectTrigger data-testid="dialog-status" className={`w-52 ${selTrig}`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-navy-soft border-white/10 text-white">
              {STATUS_ORDER.map((s) => <SelectItem key={s} value={s} className="focus:bg-white/10 focus:text-white">{STATUS_META[s].label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={lead.assigned_to || "unassigned"} onValueChange={(v) => setAssigned(v === "unassigned" ? "" : v)}>
            <SelectTrigger data-testid="dialog-assign" className={`w-52 ${selTrig}`}><SelectValue placeholder="Assign to…" /></SelectTrigger>
            <SelectContent className="bg-navy-soft border-white/10 text-white">
              <SelectItem value="unassigned" className="focus:bg-white/10 focus:text-white">Unassigned</SelectItem>
              {team.map((t) => <SelectItem key={t.id} value={t.name} className="focus:bg-white/10 focus:text-white">{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <button data-testid="dialog-delete" onClick={del} className="ml-auto flex items-center gap-2 rounded-sm border border-red-400/30 px-3 py-1.5 text-xs text-red-300 hover:bg-red-400/10 transition-colors duration-300">
            <Trash2 size={14} /> Delete
          </button>
        </div>

        <div className="mt-2 grid gap-4 rounded-sm border border-white/10 bg-white/[0.03] p-5 sm:grid-cols-2">
          {field(Mail, "Email", <a className="hover:text-gold" href={`mailto:${lead.email}`}>{lead.email}</a>)}
          {field(Phone, "Phone", <a className="hover:text-gold" href={`tel:${lead.phone}`}>{lead.phone}</a>)}
          {field(Briefcase, "Profession", lead.profession)}
          {field(Briefcase, "Employment", lead.employment_structure)}
          {field(DollarSign, "Income Range", lead.income_range)}
          {field(MessageSquare, "Preferred Contact", lead.preferred_contact)}
          <div className="sm:col-span-2">{field(MessageSquare, "Heard About Us", lead.lead_source)}</div>
          <div className="sm:col-span-2">{field(MessageSquare, "Primary Concern", lead.primary_concern)}</div>
          {lead.message ? <div className="sm:col-span-2">{field(MessageSquare, "Message", lead.message)}</div> : null}
        </div>

        <div>
          <p className="mb-3 text-xs uppercase tracking-[0.15em] text-gold">Notes & Activity</p>
          <div className="space-y-3">
            {(lead.notes || []).length === 0 && <p className="text-sm text-white/40">No notes yet.</p>}
            {(lead.notes || []).slice().reverse().map((n) => (
              <div key={n.id} className="rounded-sm border border-white/10 bg-white/[0.02] p-3">
                <p className="text-sm text-white/85">{n.text}</p>
                <p className="mt-1 text-xs text-white/40">{n.author} · {new Date(n.at).toLocaleString()}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 flex gap-2">
            <Textarea data-testid="dialog-note-input" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note or log a call…"
              className="min-h-[44px] bg-transparent border-white/20 text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-gold" />
            <button data-testid="dialog-note-add" onClick={addNote} disabled={saving}
              className="shrink-0 self-end rounded-sm bg-gold px-4 py-2.5 text-navy-deep hover:bg-gold-hover transition-colors duration-300">
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [leads, setLeads] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s, t] = await Promise.all([
        crm.get("/leads"), crm.get("/leads/stats"), crm.get("/team")
      ]);
      setLeads(l.data); setStats(s.data); setTeam(t.data);
    } catch (err) {
      if (err.response?.status === 401) { logout(); }
      else toast.error("Could not load data.");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      // Handle Emergent Google Auth callback: /admin#session_id=XXX
      const hash = window.location.hash || "";
      const match = hash.match(/session_id=([^&]+)/);
      if (match) {
        try {
          const { data } = await crm.post("/auth/google/session", {}, {
            headers: { "X-Session-ID": decodeURIComponent(match[1]) }
          });
          localStorage.setItem("mag_token", data.token);
          window.history.replaceState(null, "", "/admin");
          setUser(data.user);
          await loadAll();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Google sign-in was not authorized.");
          window.history.replaceState(null, "", "/admin");
        } finally { setChecking(false); }
        return;
      }
      if (!localStorage.getItem("mag_token")) { setChecking(false); return; }
      try {
        const { data } = await crm.get("/auth/me");
        setUser(data);
        await loadAll();
      } catch { localStorage.removeItem("mag_token"); }
      finally { setChecking(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try { await crm.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("mag_token");
    setUser(null); setLeads([]); setStats(null);
  };

  const onLogin = async (u) => { setUser(u); await loadAll(); };

  const visible = useMemo(() => {
    let list = leads;
    if (filter !== "all") list = list.filter((l) => l.status === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((l) =>
        [l.name, l.email, l.profession].some((v) => (v || "").toLowerCase().includes(q)));
    }
    return list;
  }, [leads, filter, search]);

  const applyChange = (updated) => {
    setLeads((ls) => ls.map((l) => (l.id === updated.id ? updated : l)));
    setSelected(updated);
    crm.get("/leads/stats").then((r) => setStats(r.data)).catch(() => {});
  };
  const applyDelete = (id) => {
    setLeads((ls) => ls.filter((l) => l.id !== id));
    setSelected(null);
    crm.get("/leads/stats").then((r) => setStats(r.data)).catch(() => {});
  };

  if (checking) return <div className="grid min-h-screen place-items-center bg-navy text-white/60">Loading…</div>;
  if (!user) return <Login onLogin={onLogin} />;

  return (
    <div className="min-h-screen bg-navy px-4 py-8 sm:px-6 lg:px-10">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Matlock" className="h-12 w-auto" />
            <div>
              <h1 className="font-serif text-3xl text-white">Consultation CRM</h1>
              <p className="text-sm text-white/50">Signed in as {user.name}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button data-testid="crm-export" onClick={() => exportLeadsCsv(visible)} className="flex items-center gap-2 rounded-sm border border-white/15 px-4 py-2 text-sm text-white/80 hover:border-gold hover:text-gold transition-colors duration-300"><Download size={15} /> Export CSV</button>
            <button data-testid="crm-refresh" onClick={loadAll} className="flex items-center gap-2 rounded-sm border border-white/15 px-4 py-2 text-sm text-white/80 hover:border-gold hover:text-gold transition-colors duration-300"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh</button>
            <button data-testid="crm-logout" onClick={logout} className="flex items-center gap-2 rounded-sm border border-white/15 px-4 py-2 text-sm text-white/80 hover:border-gold hover:text-gold transition-colors duration-300"><LogOut size={15} /> Log out</button>
          </div>
        </div>

        {stats && (
          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
            <div className="rounded-sm border border-white/10 bg-white/[0.03] p-4">
              <p className="text-xs uppercase tracking-[0.12em] text-white/40">Total</p>
              <p className="mt-1 font-serif text-3xl text-white">{stats.total}</p>
            </div>
            <div className="rounded-sm border border-gold/30 bg-gold/5 p-4">
              <p className="text-xs uppercase tracking-[0.12em] text-gold">New / 7 days</p>
              <p className="mt-1 font-serif text-3xl text-white">{stats.new_this_week}</p>
            </div>
            {STATUS_ORDER.map((s) => (
              <div key={s} className="rounded-sm border border-white/10 bg-white/[0.03] p-4">
                <p className="truncate text-xs uppercase tracking-[0.12em]" style={{ color: STATUS_META[s].color }}>{STATUS_META[s].label}</p>
                <p className="mt-1 font-serif text-3xl text-white">{stats.by_status[s] ?? 0}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input data-testid="crm-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, profession…"
              className="bg-transparent border-white/20 pl-9 text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-gold" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="crm-filter" className="w-52 bg-transparent border-white/20 text-white focus:ring-0 focus:border-gold">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-navy-soft border-white/10 text-white">
              <SelectItem value="all" className="focus:bg-white/10 focus:text-white">All statuses</SelectItem>
              {STATUS_ORDER.map((s) => <SelectItem key={s} value={s} className="focus:bg-white/10 focus:text-white">{STATUS_META[s].label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="mt-6 space-y-3" data-testid="crm-leads-list">
          {visible.length === 0 && <p className="text-white/50">No leads match.</p>}
          {visible.map((l) => (
            <button key={l.id} data-testid="crm-lead-row" onClick={() => setSelected(l)}
              className="flex w-full flex-wrap items-center justify-between gap-3 rounded-sm border border-white/10 bg-white/[0.03] p-5 text-left transition-colors duration-300 hover:border-gold/40 hover:bg-white/[0.05]">
              <div className="min-w-[180px]">
                <p className="font-serif text-xl text-white">{l.name}</p>
                <p className="text-sm text-white/50">{l.profession} · {l.income_range}</p>
              </div>
              <div className="hidden text-sm text-white/60 md:block">
                <p>{l.email}</p><p>{l.phone}</p>
              </div>
              <div className="flex items-center gap-3">
                {l.assigned_to ? <span className="text-xs text-white/50">{l.assigned_to}</span> : null}
                <StatusPill status={l.status} />
                <span className="text-xs text-white/40">{new Date(l.created_at).toLocaleDateString()}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <LeadDialog lead={selected} team={team} onClose={() => setSelected(null)} onChange={applyChange} onDelete={applyDelete} />
    </div>
  );
}
